import {
  IAssets,
  IProducts as IGcfProducts,
  IProductVariations as IGcfProductVariations,
} from 'gcf-components/interfaces';
import {
  compact,
  entries,
  first,
  get,
  groupBy,
  isNil,
  set,
  toString,
} from 'lodash';
import {
  type IGcfProductList,
  type IGcfProductListGroup,
  type IGcfProductListGroups,
  type IGcfProductListProduct,
  type IGcfProductListProducts,
  type IGcfProductListProductSizes,
  type IGcfProductListProductSizeVariations,
  GcfGroupProductType,
  IAssemblyComponent,
  ICCAsset,
  IComponent,
  IGcfProductListProductImage,
  IProduct,
  IProductVariation,
  IThemeRes,
} from 'src/interfaces';
import { ComponentTypes } from 'src/interfaces/ComponentTypesList';
import { uid } from 'uid';

import { getAssetFromAssetId } from './gcf-helper';
import { naturalSort } from './util-functions';

/**
 * Extracts unique product codes from assembly components and components arrays
 * @param assemblyComponents Array of assembly components
 * @param components Array of component objects containing product codes
 * @returns Array of unique product codes
 */
export const getAssemblyProductCodes = (
  assemblyComponents: IAssemblyComponent[] = [],
  components: IComponent[] = [],
): string[] => {
  const productCodes: string[] = [];

  const hasComponentData = assemblyComponents.some((ac) => ac.component);
  if (assemblyComponents.length > 0 && hasComponentData) {
    assemblyComponents.forEach((ac) => {
      const lineupCodes =
        ac.component?.config?.lineup?.map(
          (lineupItem: any) => lineupItem.productCode,
        ) || [];
      productCodes.push(...lineupCodes);

      const componentProductCodes =
        ac?.component?.product_variation_product_code || [];
      productCodes.push(...componentProductCodes);

      const productList = ac.component?.config?.productList;
      if (productList && productList.groups) {
        Object.values(productList.groups).forEach((group: any) => {
          if (group.products) {
            Object.values(group.products).forEach((product: any) => {
              if (product.productCode) {
                productCodes.push(product.productCode);
              }

              if (product.sizes) {
                Object.values(product.sizes).forEach((size: any) => {
                  if (size.variations) {
                    Object.values(size.variations).forEach((variation: any) => {
                      if (variation.productCode) {
                        productCodes.push(variation.productCode);
                      }
                    });
                  }
                });
              }

              if (product.bundleImages) {
                product.bundleImages.forEach((bundleImage: any) => {
                  if (bundleImage.productCode) {
                    productCodes.push(bundleImage.productCode);
                  }
                });
              }
            });
          }
          if (group.backups) {
            Object.values(group.backups).forEach((backup: any) => {
              if (backup.productCode) {
                productCodes.push(backup.productCode);
              }

              if (backup.sizes) {
                Object.values(backup.sizes).forEach((size: any) => {
                  if (size.variations) {
                    Object.values(size.variations).forEach((variation: any) => {
                      if (variation.productCode) {
                        productCodes.push(variation.productCode);
                      }
                    });
                  }
                });
              }
            });
          }
        });
      }
    });
  }

  if (components.length > 0) {
    components.forEach((component) => {
      const lineupCodes =
        component?.config?.lineup?.map(
          (lineupItem: any) => lineupItem.productCode,
        ) || [];
      productCodes.push(...lineupCodes);

      const variationCodes = component?.product_variation_product_code || [];
      productCodes.push(...variationCodes);

      const productList = component?.config?.productList;
      if (productList && productList.groups) {
        Object.values(productList.groups).forEach((group: any) => {
          if (group.products) {
            Object.values(group.products).forEach((product: any) => {
              if (product.productCode) {
                productCodes.push(product.productCode);
              }

              if (product.sizes) {
                Object.values(product.sizes).forEach((size: any) => {
                  if (size.variations) {
                    Object.values(size.variations).forEach((variation: any) => {
                      if (variation.productCode) {
                        productCodes.push(variation.productCode);
                      }
                    });
                  }
                });
              }

              if (product.bundleImages) {
                product.bundleImages.forEach((bundleImage: any) => {
                  if (bundleImage.productCode) {
                    productCodes.push(bundleImage.productCode);
                  }
                });
              }
            });
          }
          if (group.backups) {
            Object.values(group.backups).forEach((backup: any) => {
              if (backup.productCode) {
                productCodes.push(backup.productCode);
              }

              if (backup.sizes) {
                Object.values(backup.sizes).forEach((size: any) => {
                  if (size.variations) {
                    Object.values(size.variations).forEach((variation: any) => {
                      if (variation.productCode) {
                        productCodes.push(variation.productCode);
                      }
                    });
                  }
                });
              }
            });
          }
        });
      }

      const configItems = component?.config?.items;
      if (configItems && configItems.groups) {
        Object.values(configItems.groups).forEach((group: any) => {
          if (group.products) {
            Object.values(group.products).forEach((product: any) => {
              if (product.productCode) {
                productCodes.push(product.productCode);
              }
            });
          }
        });
      }
    });
  }

  return [...new Set(productCodes.filter(Boolean))].sort();
};

/**
 * Extracts unique component type IDs from assembly components
 * @param assemblyComponents Array of assembly components
 * @returns Array of unique component type IDs
 */
export const getAssemblyComponentTypeIds = (
  assemblyComponents: IAssemblyComponent[] = [],
): number[] => {
  const componentTypeIds = assemblyComponents
    .map((ac) => ac.component?.component_type_id)
    .filter(Boolean) as number[];

  return [...new Set(componentTypeIds)];
};

/**
 * Generates product filters for assembly components
 * @param assemblyComponents Array of assembly components
 * @returns Object containing product and variation filters
 */
export const generateAssemblyProductFilters = (
  assemblyComponents: IAssemblyComponent[] = [],
  components: IComponent[] = [],
): { productFilters: string[]; productVariationFilters: string[] } => {
  const productCodes = getAssemblyProductCodes(assemblyComponents, components);

  const productFilters: string[] = [];
  const productVariationFilters: string[] = [];

  if (productCodes.length > 0) {
    productFilters.push(`product_code.list=${productCodes.join('|')}`);
    productVariationFilters.push(
      `group_product_code.list=${productCodes.join('|')}`,
    );
  }

  return { productFilters, productVariationFilters };
};

export const mapProductsToDatasourceJSON = (
  products: IProduct[],
  themes: IThemeRes[],
): IGcfProducts => {
  return products.reduce((acc, product) => {
    const productAsset = first(product.assets);

    return {
      ...acc,
      [product.product_code]: {
        productCode: product.product_code,
        nameOverride: product.name_override,
        assets: { default: [productAsset?.name || ''] },
        active: product.active,
        categories: product.productCategories.map((category) => category.name),
        variations: mapProductVariationsToDatasourceJSON(
          product.productVariations,
          themes,
        ),
        createdAt: product.created_at,
        modifiedAt: product.modified_at,
        name:
          product.productVariations.find(
            (item) => item.product_code === product.product_code,
          )?.name ?? '',
      },
    };
  }, {}) as IGcfProducts;
};

const mapProductVariationsToDatasourceJSON = (
  productVariations: IProductVariation[],
  themes: IThemeRes[],
): IGcfProductVariations => {
  return productVariations.reduce((acc, productVariation) => {
    const productVariationAsset = first(productVariation.assets);
    const productAssets = mapVariationAssets(
      productVariation,
      themes,
      productVariation.assets ?? [],
    );

    return {
      ...acc,
      [productVariation.product_code]: {
        size: productVariation.productVariationSize?.name ?? '',
        shortCode: productVariation.productVariationSize?.short ?? '',
        productCode: productVariation.product_code,
        assetName: productVariationAsset?.name || '',
        assets: productAssets,
        name: productVariation.name,
        description: productVariation.description,
        disclaimer: productVariation.disclaimer,
        volume: productVariation.volume,
        flavor: productVariation.productVariationFlavor?.name ?? '',
        nutrition: productVariation.nutrition,
        active: productVariation.active,
        legal: productVariation.legal,
        createdAt: productVariation.created_at,
        modifiedAt: productVariation.modified_at,
        availableInPricing: true, // mock pricing file availability in cc-ui
      },
    };
  }, {} as IGcfProductVariations);
};

export const canComponentEditProducts = (
  componentTypeName?: ComponentTypes,
) => {
  switch (componentTypeName) {
    case ComponentTypes.SINGLE_SHELF:
    case ComponentTypes.PROMOTIONAL_SPACE:
    case ComponentTypes.PROMOTIONAL_COMPONENT:
    case ComponentTypes.LISTING_WITH_OPTIONAL_VISUALS:
    case ComponentTypes.LISTING_VISUALS_GALLERY:
      return true;

    default:
      return false;
  }
};

export const extractProductsFromGCFGroups = (
  groups?: IGcfProductListGroups,
) => {
  let products: IGcfProductListProduct[] = [];
  let backups: IGcfProductListProduct[] = [];

  for (const groupNumber in groups) {
    const { products: gcfProducts, backups: gcfBackups } =
      extractProductsFromGCFGroup(groups[groupNumber]);

    products = products.concat(gcfProducts);
    backups = backups.concat(gcfBackups);
  }

  return { products, backups };
};

export const extractProductsFromGCFGroup = (group?: IGcfProductListGroup) => {
  const products = entries(group?.products).map(([, gcfProductValue]) => {
    return {
      ...gcfProductValue,
      config: { ...gcfProductValue?.config, isBackupProduct: false },
    } as IGcfProductListProduct;
  });
  const backups = entries(group?.backups).map(([, gcfProductValue]) => {
    return {
      ...gcfProductValue,
      config: { ...gcfProductValue?.config, isBackupProduct: true },
    } as IGcfProductListProduct;
  });

  return { products, backups };
};

export const extractProductsFromGCF = (
  productList: IGcfProductList,
  groupNumber: number,
) => {
  const { products: gcfProducts, backups: gcfBackups } = get(
    productList,
    [`groups`, toString(groupNumber)],
    {},
  ) as IGcfProductListGroup;

  const products = entries(gcfProducts).map(([, gcfProductValue]) => {
    return gcfProductValue;
  });

  const backups = entries(gcfBackups).map(([, gcfBackupValue]) => {
    return gcfBackupValue;
  });

  return { products, backups };
};

export const mapGcfGroupProducts = (
  groupNumber: number,
  group?: IGcfProductListGroup,
  gcfProducts: IGcfProductListProduct[] = [],
  gcfBackups: IGcfProductListProduct[] = [],
  sortArray = true,
): IGcfProductListGroup => {
  const newGroup = group
    ? { ...group }
    : { order: groupNumber, products: {}, backups: {}, config: {} };

  set(
    newGroup,
    GcfGroupProductType.PRODUCTS,
    mapGCFProductArrayToGcfGroupProducts(gcfProducts, sortArray),
  );

  set(
    newGroup,
    GcfGroupProductType.BACKUPS,
    mapGCFProductArrayToGcfGroupProducts(gcfBackups, sortArray),
  );

  return newGroup;
};

export const sortItemsByOrder = <T extends { order?: number }>(a: T, b: T) => {
  if (!a.order) return 1;
  if (!b.order) return -1;

  return a.order - b.order;
};

export const mapGCFProductArrayToGcfGroupProducts = (
  gcfProducts: IGcfProductListProduct[],
  sortArray = true,
) => {
  const sortedOrOriginalProducts = sortArray
    ? gcfProducts.sort(sortItemsByOrder)
    : gcfProducts;

  return sortedOrOriginalProducts
    .map((item, index) => ({ ...item, order: index + 1 }))
    .reduce((acc, product) => {
      acc[toString(product.order)] = product;

      return acc;
    }, {} as IGcfProductListProducts);
};

export const mapProductsToGCFProductArray = (
  products: IProduct[],
  productOrderStartAt: number = 1,
  showProductVariation: boolean = false,
) => {
  return products.map((product, index) =>
    mapProductToGCFGroupProduct(
      product,
      productOrderStartAt + index,
      showProductVariation,
    ),
  );
};

export const mapProductToGCFGroupProduct = (
  product: IProduct,
  productOrder: number,
  showProductVariation: boolean = false,
  overrideSizeConfig?: Record<
    'showCalories' | 'showLabel' | 'showPrice' | 'showFlavors',
    boolean
  >,
  overrideImageAttributes?: Pick<
    IGcfProductListProductImage,
    'style' | 'outageStamp'
  >,
): IGcfProductListProduct => {
  const reorderedProductVariations = naturalSort(
    product.productVariations,
    'productVariationSize.name',
  );

  const defaultSize = first(reorderedProductVariations)?.productVariationSize
    ?.name;

  return {
    uid: uid(),
    order: productOrder,
    productCode: product.product_code,
    config: {
      defaultSize: showProductVariation ? defaultSize : undefined,
      nameOverride: '',
    },
    bundleImages: [],
    sizes: showProductVariation
      ? mapProductVariationsToSizes(
          product.productVariations,
          defaultSize,
          overrideSizeConfig,
          overrideImageAttributes,
        )
      : {},
  };
};

export const mapProductsToGCFGroup = (
  products: IProduct[],
  groupNumber: number,
) => {
  const gcfProducts = mapProductsToGCFProductArray(products, undefined, true);

  return {
    order: groupNumber,
    products: mapGCFProductArrayToGcfGroupProducts(gcfProducts),
    backups: {},
    config: {},
  } as IGcfProductListGroup;
};

export const hasUniqueSizeAndFlavorVariations = (
  productVariations: IProductVariation[],
) => {
  const variationMap = new Map<string, number>();

  for (const variation of productVariations) {
    const key = `${variation.product_variation_size_id}-${variation.product_variation_flavor_id}`;
    if (variationMap.has(key)) {
      return false;
    }
    variationMap.set(key, 1);
  }

  return true;
};

const mapVariationAssets = (
  productVariation: IProductVariation,
  themes: IThemeRes[],
  assets: ICCAsset[],
) => {
  return themes.reduce((assetsAcc, theme) => {
    return {
      ...assetsAcc,
      [theme.name]: compact(
        productVariation.asset_id.map((assetId) => {
          const asset = getAssetFromAssetId(assets, assetId);

          if (asset?.theme_id.includes(theme.id)) {
            return asset.name;
          }

          return null;
        }),
      ),
    };
  }, {} as IAssets);
};

const mapProductVariationsToSizes = (
  productVariations: IProductVariation[],
  defaultSize?: string,
  overrideSizeConfig?: Record<
    'showCalories' | 'showLabel' | 'showPrice' | 'showFlavors',
    boolean
  >,
  overrideImageAttributes?: Pick<
    IGcfProductListProductImage,
    'style' | 'outageStamp'
  >,
) => {
  const validVariations = productVariations.filter(
    (v) => !isNil(v.productVariationSize?.name),
  );
  const groupedVariations = groupBy(
    validVariations,
    'productVariationSize.name',
  );

  const reorderedKeys = naturalSort(Object.keys(groupedVariations));

  return reorderedKeys.reduce((acc, sizeName, sizeIndex) => {
    acc[sizeName] = {
      order: sizeIndex + 1,
      config: overrideSizeConfig ?? {
        showCalories: !sizeIndex,
        showLabel: !sizeIndex,
        showPrice: !sizeIndex,
        showFlavors: !sizeIndex,
      },
      variations: mapProductVariationsToFlavors(
        get(groupedVariations, sizeName),
        !isNil(defaultSize)
          ? defaultSize
          : sizeIndex === 0
          ? sizeName
          : undefined,
        overrideImageAttributes,
      ),
      name: sizeName,
    };

    return acc;
  }, {} as IGcfProductListProductSizes);
};

const getVariationConfig = (
  variation: IProductVariation,
  flavorIndex: number,
  defaultSize?: string,
  overrideImageAttributes?: Pick<
    IGcfProductListProductImage,
    'style' | 'outageStamp'
  >,
) => {
  if (
    defaultSize === variation.productVariationSize?.name &&
    flavorIndex === 0
  ) {
    return {
      images: [
        {
          visible: true,
          ...(overrideImageAttributes ?? {}),
        },
      ],
    };
  }

  if (overrideImageAttributes) {
    return {
      images: [
        {
          ...overrideImageAttributes,
        },
      ],
    };
  }

  return {};
};

const mapProductVariationsToFlavors = (
  productVariations: IProductVariation[] = [],
  defaultSize?: string,
  overrideImageAttributes?: Pick<
    IGcfProductListProductImage,
    'style' | 'outageStamp'
  >,
) => {
  return productVariations.reduce((acc, variation, flavorIndex) => {
    acc[variation.product_code] = {
      order: flavorIndex + 1,
      config: getVariationConfig(
        variation,
        flavorIndex,
        defaultSize,
        overrideImageAttributes,
      ),
      productCode: variation.product_code,
    };

    return acc;
  }, {} as IGcfProductListProductSizeVariations);
};
